
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FeedbackTicket } from '../types';

interface FeedbackTableProps {
  tickets: FeedbackTicket[];
  loading: boolean;
  error?: unknown;
  onTicketClick: (ticket: FeedbackTicket) => void;
}

export const FeedbackTable: React.FC<FeedbackTableProps> = ({
  tickets,
  loading,
  error,
  onTicketClick,
}) => {
  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10 text-destructive">
        An error occurred while loading tickets. Please try again later.
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="text-center py-10 text-muted-foreground">
        No feedback tickets found matching your criteria.
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Urgency</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tickets.map((ticket) => (
            <TableRow key={ticket.id}>
              <TableCell className="whitespace-nowrap">
                {new Date(ticket.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell className="max-w-[150px] truncate">
                {ticket.user_info?.full_name || 'Unknown'}
              </TableCell>
              <TableCell>
                <Badge variant={
                  ticket.category === 'Bug' ? "destructive" :
                  ticket.category === 'Feature Request' ? "default" : "secondary"
                }>
                  {ticket.category}
                </Badge>
              </TableCell>
              <TableCell className="max-w-[100px] truncate">
                {ticket.page_source || 'Unknown'}
              </TableCell>
              <TableCell className="max-w-[180px] truncate">
                {ticket.description}
              </TableCell>
              <TableCell>
                <Badge variant={
                  ticket.urgency === 'Critical' ? "destructive" :
                  ticket.urgency === 'High' ? "destructive" :
                  ticket.urgency === 'Medium' ? "default" : "secondary"
                }>
                  {ticket.urgency}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge variant={
                  ticket.status === 'Open' ? "outline" :
                  ticket.status === 'In Progress' ? "default" : "secondary"
                }>
                  {ticket.status}
                </Badge>
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onTicketClick(ticket)}
                >
                  View
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
